import { useTranslation } from '@wix/fe-essentials/react';
import { GroupExpansionStyle } from '@wix/rules-panel-ui';
import React, { useCallback, useMemo, useRef } from 'react';
import { usePanelStateAtom } from '../../../../hooks/rules-state';
import { useBiLogger } from '../../../../hooks/use-bi-logger';
import { getSubtitles } from '../../../../rule-mappers/build-rule-props/get-subtitles';
import { getNumberOfIssuesInVerticalGroup } from '../../../../rule-mappers/shared/helpers';
import { useVerify } from '../../../../rule-mappers/shared/use-verify';
import { resolveGroupAnalysisState } from '../../../../utils/resolve-group-analysis-state';
import { RuleResultsSkeleton } from '../../rule-results-skeleton';
import { useRuleGroupUtils } from './rule-group-utils';

// Overload 1: For Buttons

// Overload 2: For Collapsibles

export function useBasePageGroup(_ref) {
  let {
    pageId,
    ruleResults = [],
    onGroupToggleCallback,
    expansionMode = GroupExpansionStyle.collapsible
  } = _ref;
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    getGroupData,
    panelState,
    getPagesData
  } = usePanelStateAtom();
  const {
    verify
  } = useVerify({});
  const {
    getBadge,
    buildGroupNodes,
    getIssueCount
  } = useRuleGroupUtils();
  const isAnalyzingRef = useRef();
  const groupData = getGroupData(pageId);
  if (!groupData) {
    throw new Error(`Group data not found for pageId: ${pageId}.`);
  }
  const {
    analysisState,
    rulesStateMap,
    title,
    itemType
  } = groupData;
  const {
    isFirstAnalysis,
    isAnalyzeFailed,
    isPendingResults,
    isFetchResultsFailed,
    isFetchResultSucceeded,
    isPerformingAnalysis
  } = useMemo(() => resolveGroupAnalysisState(analysisState), [analysisState]);
  const pageIdToPageData = getPagesData();
  const subtitles = useMemo(() => {
    var _pageIdToPageData$get;
    return getSubtitles({
      groupData,
      scannedPagesAmount: groupData.scannedPages ?? 0,
      t,
      totalPagesToScan: ((_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null || (_pageIdToPageData$get = _pageIdToPageData$get.innerRoutes) == null ? void 0 : _pageIdToPageData$get.length) ?? 0
    });
  }, [groupData, pageId, pageIdToPageData, t]);
  const numberOfIssues = useMemo(() => {
    if (expansionMode === GroupExpansionStyle.button) {
      return isFetchResultSucceeded ? getNumberOfIssuesInVerticalGroup(panelState, itemType) : 0;
    }
    return getIssueCount(rulesStateMap);
  }, [expansionMode, getIssueCount, rulesStateMap, isFetchResultSucceeded, panelState, itemType]);
  const handleVerifyClick = useCallback(() => {
    const globalComponentIds = panelState.globalSectionComponentsDataMap ? Array.from(panelState.globalSectionComponentsDataMap.values()).map(_ref2 => {
      let {
        componentId
      } = _ref2;
      return componentId;
    }) : [];
    return verify(groupData, globalComponentIds);
  }, [verify, groupData, panelState.globalSectionComponentsDataMap]);
  const onGroupToggle = useCallback(function (toggleType, groupIndex, initiatedByUser) {
    if (initiatedByUser === void 0) {
      initiatedByUser = false;
    }
    biLogger.pageExpandCollapse({
      initiatedByUser,
      pageId,
      pageIndex: groupIndex,
      pageType: itemType,
      ruleResults,
      toggleType
    });
    onGroupToggleCallback == null || onGroupToggleCallback(toggleType, groupIndex, initiatedByUser);
  }, [biLogger, pageId, itemType, ruleResults, onGroupToggleCallback]);
  if (isAnalyzingRef.current && !isPerformingAnalysis) {
    var _groupData$analysisDa;
    biLogger.pageScanFinished({
      analysisJobId: ((_groupData$analysisDa = groupData.analysisData) == null ? void 0 : _groupData$analysisDa.analysisJobId) ?? '',
      issuesCount: isAnalyzeFailed ? -1 : numberOfIssues,
      pageId,
      pageType: groupData.itemType
    });
  }
  isAnalyzingRef.current = isPerformingAnalysis;
  const {
    collapsedNode,
    expandNode
  } = buildGroupNodes(isFirstAnalysis, isAnalyzeFailed, isPerformingAnalysis, {
    numberOfIssues,
    onClick: handleVerifyClick,
    pageId,
    taskCount: ruleResults.length
  });
  const ruleGroupProps = useMemo(() => {
    const baseProps = {
      disabled: isFirstAnalysis || isAnalyzeFailed,
      groupId: pageId,
      onGroupToggle,
      ruleGroupContent: {
        badge: getBadge(isAnalyzeFailed, isPerformingAnalysis, numberOfIssues),
        badgeTooltipContent: isAnalyzeFailed ? t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
        collapsedNode,
        expandNode,
        groupLabel: title,
        subtitles
      },
      ruleResults
    };
    if (expansionMode === GroupExpansionStyle.button) {
      return {
        ...baseProps,
        expansionMode: GroupExpansionStyle.button
      };
    } else {
      return {
        ...baseProps,
        expansionMode: GroupExpansionStyle.collapsible,
        isFetchResultsFailed,
        isLoading: isPendingResults || isPerformingAnalysis,
        ruleResultsSkeletonComponent: /*#__PURE__*/React.createElement(RuleResultsSkeleton, {
          pageId: pageId
        })
      };
    }
  }, [isFirstAnalysis, isAnalyzeFailed, pageId, getBadge, isPerformingAnalysis, numberOfIssues, t, collapsedNode, expandNode, title, subtitles, expansionMode, onGroupToggle, isFetchResultsFailed, isPendingResults, ruleResults]);
  return {
    ruleGroupProps
  };
}
//# sourceMappingURL=use-base-page-group.js.map